import { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CalendarClock,
  LogOut,
  CreditCard,
  RefreshCw,
  Save,
  Shield,
  Trash2,
  UserPlus,
  Users,
  Handshake,
  Lock,
  Unlock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Copy,
  KeyRound,
  Tag,
  Pencil,
  X,
  Eye,
  Search,
  Download,
  TrendingUp,
  Wallet,
  AlertTriangle,
  Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Alert from '../components/ui/Alert';
import MetricCard from '../components/ui/MetricCard';
import SectionCard from '../components/ui/SectionCard';
import PageHeader from '../components/shared/PageHeader';
import PlanBrand, { isSynesPlan } from '../components/shared/PlanBrand';
import GDNFinancePanel from '../components/gdn/GDNFinancePanel';
import GDNClientDetailModal from '../components/gdn/GDNClientDetailModal';
import Select from '../components/ui/Select';
import Table from '../components/ui/Table';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { COUNTRY_PRESETS, getCountryPreset } from '../lib/localization';
import { currency } from '../utils/formatters';
import { maskCpfCnpj, maskPhone, normalizeMoney } from '../utils/masks';

function defaultDueDate() {
  return new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function createSlug(value) {
  const base = value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${base || 'oficina'}-${Date.now().toString(36)}`;
}

function createReferralCode(name, email) {
  const base = `${name || email || 'parceiro'}`
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 32) || 'parceiro';
  return `${base}-${Math.random().toString(36).slice(2, 7)}`;
}

function partnerReferralUrl(code) {
  if (!code) return '';
  const baseUrl = process.env.REACT_APP_MECSYNC_PUBLIC_URL || 'https://mecsync.com.br';
  return `${baseUrl.replace(/\/$/, '')}/teste-gratis?ref=${encodeURIComponent(code)}`;
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(`${String(value).split('T')[0]}T00:00:00`).toLocaleDateString('pt-BR');
}

function getMonthKey(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 7);
}

function billingCurrency(value, moeda = 'BRL') {
  const currencyCode = String(moeda || 'BRL').toUpperCase();
  return currency(value, {
    currency: currencyCode,
    locale: currencyCode === 'BRL' ? 'pt-BR' : 'en-US',
  });
}

const emptyClientForm = {
  oficina_id: '',
  nome_oficina: '',
  responsavel: '',
  email: '',
  senha: '',
  telefone: '',
  documento: '',
  pais_codigo: 'BR',
  plano_id: '',
  perfil_conta: 'admin',
  valor_mensal: '',
  moeda: 'BRL',
  provedor_pagamento: 'mercado_pago',
  data_vencimento: defaultDueDate(),
  tipo_acesso: 'paid',
  trial_dias: 30,
  sales_responsibilities: [],
};

function regionalPayload(countryCode) {
  const settings = getCountryPreset(countryCode || 'BR');
  return {
    pais_codigo: settings.countryCode,
    idioma: settings.language,
    locale_codigo: settings.locale,
    moeda: settings.currency,
    fuso_horario: settings.timeZone,
    sistema_medidas: settings.measurementSystem,
    formato_data: settings.dateFormat,
  };
}

const emptyEmployeeForm = {
  oficina_id: '',
  nome: '',
  email: '',
  telefone: '',
  senha: '',
  perfil: 'mecanico',
};

const emptyOfferForm = {
  id: null,
  nome: '',
  plano_id: '',
  valor: '',
  moeda: 'BRL',
  tipo_cobranca: 'monthly',
  trial_dias: 30,
  validade_inicio: '',
  validade_fim: '',
  limite_uso: '',
  descricao: '',
};

const accountProfileOptions = [
  { value: 'admin', label: 'Admin da oficina' },
  { value: 'mecanico', label: 'Funcionario' },
];

const employeeProfileOptions = [
  { value: 'mecanico', label: 'Funcionario' },
  { value: 'admin', label: 'Admin da oficina' },
];

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: Shield },
  { id: 'clientes', label: 'Clientes', icon: Building2 },
  { id: 'testes', label: 'Testes grátis', icon: CalendarClock },
  { id: 'ofertas', label: 'Ofertas', icon: Tag },
  { id: 'cadastro', label: 'Novo cliente', icon: UserPlus },
  { id: 'equipe', label: 'Funcionários', icon: Users },
  { id: 'parceiros', label: 'Parceiros', icon: Handshake },
  { id: 'financeiro', label: 'Financeiro', icon: CreditCard },
  { id: 'auditoria', label: 'Auditoria', icon: Shield },
];

export default function PainelGDN() {
  const { user, perfil, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planPrices, setPlanPrices] = useState([]);
  const [offices, setOffices] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [onlineBookings, setOnlineBookings] = useState([]);
  const [financeParticipants, setFinanceParticipants] = useState([]);
  const [salesResponsibilities, setSalesResponsibilities] = useState([]);
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [partnerDrafts, setPartnerDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingClient, setSavingClient] = useState(false);
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [formFeedback, setFormFeedback] = useState(null);
  const [employeeFeedback, setEmployeeFeedback] = useState(null);
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
  const [deleteClient, setDeleteClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resettingUser, setResettingUser] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [blockClient, setBlockClient] = useState(null);
  const [blockingClient, setBlockingClient] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [unblockClient, setUnblockClient] = useState(null);
  const [unblockingClient, setUnblockingClient] = useState(false);
  const [savingPartnerId, setSavingPartnerId] = useState(null);

  const [detailClient, setDetailClient] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState('todos');
  const [clientPlanFilter, setClientPlanFilter] = useState('todos');
  const [clientSort, setClientSort] = useState('nome');
  const [loadWarnings, setLoadWarnings] = useState([]);

  const [offers, setOffers] = useState([]);
  const [savingOffer, setSavingOffer] = useState(false);
  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [deleteOfferTarget, setDeleteOfferTarget] = useState(null);
  const [deletingOffer, setDeletingOffer] = useState(false);

  const isEditingOffer = Boolean(offerForm.id);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const [
        clientsRes,
        subscriptionsRes,
        plansRes,
        planPricesRes,
        officesRes,
        usersRes,
        authUsersRes,
        paymentsRes,
        onlineRes,
        financeParticipantsRes,
        salesResponsibilitiesRes,
        partnerApplicationsRes,
        auditLogsRes,
        offersRes,
      ] = await Promise.all([
        supabase.from('mecsync_clientes').select('*').is('removed_at', null).neq('status', 'cancelado').order('criado_em', { ascending: false }),
        supabase.from('assinaturas').select('*').order('data_vencimento', { ascending: true }),
        supabase.from('planos_mecsync').select('*').eq('ativo', true).order('nome'),
        supabase.from('mecsync_plan_prices').select('*').eq('active', true),
        supabase.from('oficinas').select('id,nome,email,telefone,plano,pais_codigo,idioma,locale_codigo,moeda,fuso_horario,sistema_medidas,formato_data').order('nome'),
        supabase.from('usuarios').select('id,oficina_id,nome,email,telefone,perfil,ativo,criado_em').eq('ativo', true).order('nome'),
        supabase.functions.invoke('admin-list-users'),
        supabase.from('mecsync_pagamentos').select('*').order('criado_em', { ascending: false }).limit(100),
        supabase.from('agendamentos_online').select('oficina_id,status,os_id,created_at'),
        supabase.from('gdn_finance_participants').select('id,nome,funcao,ativo,deleted_at').is('deleted_at', null).eq('ativo', true).order('prioridade_pagamento', { ascending: true }),
        supabase.from('gdn_sales_responsibilities_detailed').select('*').eq('ativo', true),
        supabase.from('gdn_partner_applications').select('*, gdn_finance_participants(nome,funcao,ativo)').order('created_at', { ascending: false }),
        supabase.from('admin_audit_logs').select('*').order('created_at', { ascending: false }).limit(50),
        // FIX: esta consulta estava faltando. Sem ela, `offersRes` chegava `undefined`
        // e `offersRes.error` explodia com TypeError, derrubando o load() inteiro.
        // Ajuste a coluna de ordenação ('criado_em') se o nome real for outro na sua tabela.
        supabase.from('mecsync_sales_offers').select(`
          *,
          planos_mecsync (
            id,
            codigo,
            nome,
            valor_mensal
          )
        `).order('criado_em', { ascending: false }),
      ]);

      // Consultas críticas: sem elas o painel perde a função principal.
      if (clientsRes.error) throw clientsRes.error;
      if (subscriptionsRes.error) throw subscriptionsRes.error;
      if (plansRes.error) throw plansRes.error;

      // Consultas secundárias: uma falha não pode derrubar o painel inteiro.
      // Os avisos aparecem no topo do dashboard para depuração rápida.
      const warnings = [];
      const optionalList = (label, res) => {
        if (res?.error) warnings.push(`${label}: ${res.error.message || 'falhou'}`);
        return res?.data || [];
      };
      const planPricesList = optionalList('Preços dos planos', planPricesRes);
      const officesList = optionalList('Oficinas', officesRes);
      const usersList = optionalList('Usuários das oficinas', usersRes);
      const paymentsList = optionalList('Pagamentos', paymentsRes);
      const onlineList = optionalList('Agendamentos online', onlineRes);
      const financeList = optionalList('Participantes financeiros', financeParticipantsRes);
      const responsibilitiesList = optionalList('Comissões de venda', salesResponsibilitiesRes);
      const partnersList = optionalList('Parceiros', partnerApplicationsRes);
      const auditList = optionalList('Auditoria', auditLogsRes);
      const offersList = optionalList('Ofertas', offersRes);

      let authUsers = [];
      if (authUsersRes.error || authUsersRes.data?.error) {
        warnings.push(`Acessos ativos: ${authUsersRes.data?.error || authUsersRes.error?.message || 'falhou'}`);
      } else {
        authUsers = authUsersRes.data?.users || [];
      }
      setLoadWarnings(warnings);
      const authEmailSet = new Set(authUsers.map((user) => String(user.email || '').toLowerCase()));
      const authIdByEmail = new Map(authUsers.map((user) => [String(user.email || '').toLowerCase(), user.id]));
      const activeProfiles = usersList
        .filter((user) => authEmailSet.has(String(user.email || '').toLowerCase()))
        .map((user) => ({
          ...user,
          auth_user_id: authIdByEmail.get(String(user.email || '').toLowerCase()) || null,
        }));

      const activeClients = clientsRes.data || [];
      const activeOfficeIds = new Set(activeClients.map((client) => client.oficina_id).filter(Boolean));
      const activeSubscriptionIds = new Set(
        (subscriptionsRes.data || [])
          .filter((subscription) => activeOfficeIds.has(subscription.oficina_id))
          .map((subscription) => subscription.id),
      );

      setClients(activeClients);
      setSubscriptions((subscriptionsRes.data || []).filter((subscription) => activeOfficeIds.has(subscription.oficina_id)));
      setPlans(plansRes.data || []);
      setPlanPrices(planPricesList);
      setOffices(officesList.filter((office) => activeOfficeIds.has(office.id)));
      setUsers(activeProfiles);
      setPayments(paymentsList.filter((payment) => (
        activeOfficeIds.has(payment.oficina_id) || activeSubscriptionIds.has(payment.assinatura_id)
      )));
      setOnlineBookings(onlineList.filter((booking) => activeOfficeIds.has(booking.oficina_id)));
      setFinanceParticipants(financeList);
      setSalesResponsibilities(responsibilitiesList.filter((item) => activeOfficeIds.has(item.oficina_id)));
      setPartnerApplications(partnersList);
      setAuditLogs(auditList);
      setOffers(offersList);
      setPartnerDrafts(Object.fromEntries(partnersList.map((application) => [
        application.id,
        {
          default_percentual: String(application.default_percentual ?? application.metadata?.default_percentual ?? 10),
          default_max_pagamentos: String(application.default_max_pagamentos ?? application.metadata?.default_max_pagamentos ?? 6),
          referral_code: application.referral_code || application.metadata?.referral_code || '',
          review_notes: application.review_notes || '',
        },
      ])));
    } catch (error) {
      console.error('Erro ao carregar painel GDN:', error);
      toast.error(error.message ? `Não foi possível carregar o painel GDN: ${error.message}` : 'Não foi possível carregar o painel GDN');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao sair:', error);
      toast.error('Não foi possível encerrar a sessão.');
    }
  }

  const rows = useMemo(() => (
    clients.map((client) => {
      const assinatura = subscriptions.find((item) => item.cliente_mecsync_id === client.id || item.oficina_id === client.oficina_id);
      const equipe = users.filter((user) => user.oficina_id === client.oficina_id);
      const ultimoPagamento = payments.find((payment) => payment.assinatura_id === assinatura?.id || payment.oficina_id === client.oficina_id);
      const responsaveisVenda = salesResponsibilities.filter((item) => item.cliente_mecsync_id === client.id);
      return {
        ...client,
        assinatura,
        equipe,
        ultimoPagamento,
        responsaveisVenda,
      };
    })
  ), [clients, subscriptions, users, payments, salesResponsibilities]);

  const clientStatusOf = (row) => {
    const status = String(row.assinatura?.status_pagamento || row.status || '').toLowerCase();
    if (status === 'trial') return 'trial';
    if (status === 'bloqueado') return 'bloqueado';
    if (status === 'cancelado' || status === 'canceled') return 'cancelado';
    if (status === 'atrasado' || status === 'overdue') return 'atrasado';
    if (status === 'pendente' || status === 'pending') return 'pendente';
    return 'ativo';
  };

  const filteredClientRows = useMemo(() => {
    const search = clientSearch.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (clientStatusFilter !== 'todos' && clientStatusOf(row) !== clientStatusFilter) return false;
      if (clientPlanFilter !== 'todos' && (row.assinatura?.plano || '') !== clientPlanFilter) return false;
      if (!search) return true;
      const haystack = [
        row.nome_oficina,
        row.responsavel,
        row.email,
        row.telefone,
        row.referral_code,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(search);
    });

    return [...filtered].sort((a, b) => {
      if (clientSort === 'vencimento') {
        const da = a.assinatura?.data_vencimento || '9999-12-31';
        const db = b.assinatura?.data_vencimento || '9999-12-31';
        return da.localeCompare(db);
      }
      if (clientSort === 'valor') {
        return Number(b.assinatura?.valor_mensal || 0) - Number(a.assinatura?.valor_mensal || 0);
      }
      return String(a.nome_oficina || '').localeCompare(String(b.nome_oficina || ''), 'pt-BR');
    });
  }, [rows, clientSearch, clientStatusFilter, clientPlanFilter, clientSort]);

  function exportClientsCsv() {
    const header = ['Oficina', 'Responsavel', 'Email', 'Telefone', 'Plano', 'Status', 'Vencimento', 'Valor mensal', 'Moeda', 'Equipe', 'Origem venda', 'Indicacao'];
    const lines = filteredClientRows.map((row) => [
      row.nome_oficina,
      row.responsavel,
      row.email,
      row.telefone,
      row.assinatura?.plano || '',
      row.assinatura?.status_pagamento || row.status || '',
      row.assinatura?.data_vencimento || '',
      String(row.assinatura?.valor_mensal || ''),
      row.assinatura?.moeda || '',
      String(row.equipe?.length || 0),
      row.origem_venda || '',
      row.referral_code || '',
    ].map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(';'));
    const csv = `\uFEFF${[header.join(';'), ...lines].join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clientes-mecsync-${todayISO()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const officeOptions = useMemo(() => offices.map((office) => {
    const client = clients.find((item) => item.oficina_id === office.id);
    return {
      value: office.id,
      label: client?.nome_oficina || office.nome || office.id,
    };
  }), [clients, offices]);

  const stats = useMemo(() => {
    const active = rows.filter((row) => row.status === 'ativo').length;
    const pending = rows.filter((row) => row.assinatura?.status_pagamento === 'pendente').length;
    const late = rows.filter((row) => row.assinatura?.status_pagamento === 'atrasado').length;
    const paidSubscriptions = rows.filter((row) => row.assinatura?.status_pagamento === 'ativo').length;
    const mrr = rows
      .filter((row) => row.status === 'ativo' && row.assinatura?.status_pagamento !== 'cancelado')
      .reduce((total, row) => total + Number(row.assinatura?.valor_mensal || 0), 0);
    const paid = payments
      .filter((payment) => String(payment.status || '').toLowerCase() === 'approved')
      .reduce((total, payment) => total + Number(payment.valor || payment.transaction_amount || 0), 0);
    const currentMonth = new Date().toISOString().slice(0, 7);
    const paidThisMonth = payments
      .filter((payment) => String(payment.status || '').toLowerCase() === 'approved' && getMonthKey(payment.criado_em || payment.created_at) === currentMonth)
      .reduce((total, payment) => total + Number(payment.valor || payment.transaction_amount || 0), 0);
    const onlineOffices = new Set(onlineBookings.map((item) => item.oficina_id)).size;
    const converted = onlineBookings.filter((item) => item.os_id || item.status === 'concluido').length;
    const partnerQueue = partnerApplications.filter((application) => ['novo', 'em_analise'].includes(application.status)).length;

    return {
      active,
      pending,
      late,
      paidSubscriptions,
      mrr,
      paid,
      paidThisMonth,
      employeeCount: users.length,
      onlineOffices,
      onlineTotal: onlineBookings.length,
      converted,
      partnerQueue,
      activeOffers: offers.filter((offer) => offer.ativa).length,
    };
  }, [rows, users, payments, onlineBookings, partnerApplications, offers]);

  const selectedPlan = plans.find((plan) => plan.id === clientForm.plano_id);
  const selectedPlanMonthlyValue = planMonthlyValue(selectedPlan, clientForm.moeda);
  const selectedEmployeeOffice = rows.find((row) => row.oficina_id === employeeForm.oficina_id);
  const salesResponsibilityTotal = clientForm.sales_responsibilities
    .reduce((total, item) => total + Number(item.percentual || 0), 0);

  function normalizeSalesResponsibilities(items = []) {
    return items
      .filter((item) => item.participant_id && (Number(item.percentual || 0) > 0 || Number(item.valor_fixo || 0) > 0))
      .map((item) => ({
        id: item.id || null,
        participant_id: item.participant_id,
        percentual: Number(item.percentual || 0),
        valor_fixo: Number(item.valor_fixo || 0),
        inicio_em: item.inicio_em || todayISO(),
        fim_em: item.fim_em || null,
        base_calculo: item.base_calculo || 'mensalidade_equivalente',
        max_pagamentos: Math.max(Number(item.max_pagamentos || 6), 1),
        teto_percentual_mensalidade: Math.max(Number(item.teto_percentual_mensalidade || 60), 0.01),
        teto_valor: Number(item.teto_valor || 0) > 0 ? Number(item.teto_valor) : null,
        observacao: item.observacao?.trim() || null,
      }));
  }

  function salesResponsibilitiesForClient(clientId) {
    return salesResponsibilities
      .filter((item) => item.cliente_mecsync_id === clientId)
      .map((item) => ({
        id: item.id,
        participant_id: item.participant_id,
        percentual: String(Number(item.percentual || 0)),
        valor_fixo: String(Number(item.valor_fixo || 0)),
        inicio_em: item.inicio_em || todayISO(),
        fim_em: item.fim_em || '',
        base_calculo: item.base_calculo || 'mensalidade_equivalente',
        max_pagamentos: String(Number(item.max_pagamentos || 6)),
        teto_percentual_mensalidade: String(Number(item.teto_percentual_mensalidade || 60)),
        teto_valor: item.teto_valor ? String(Number(item.teto_valor)) : '',
        pagamentos_gerados: Number(item.pagamentos_gerados || 0),
        valor_gerado: Number(item.valor_gerado || 0),
        observacao: item.observacao || '',
      }));
  }

  function planMonthlyValue(plan, moeda = 'BRL') {
    if (!plan) return '';
    const currencyCode = String(moeda || 'BRL').toUpperCase();
    const price = planPrices.find((item) => (
      String(item.plan_code || '').toLowerCase() === String(plan.codigo || '').toLowerCase()
      && String(item.currency || '').toUpperCase() === currencyCode
    ));
    return price?.monthly_value ?? plan.valor_mensal ?? '';
  }

  async function logAdminAction(action, target = {}, details = {}) {
    await supabase.from('admin_audit_logs').insert({
      actor_email: user?.email || null,
      actor_perfil: perfil?.perfil || null,
      action,
      target_type: target.type || null,
      target_id: target.id || null,
      target_email: target.email || null,
      oficina_id: target.oficina_id || null,
      details,
    });
  }

  function addSalesResponsibility() {
    setClientForm((prev) => ({
      ...prev,
      sales_responsibilities: [
        ...prev.sales_responsibilities,
        {
          participant_id: '',
          percentual: '',
          valor_fixo: '',
          inicio_em: todayISO(),
          fim_em: '',
          base_calculo: 'mensalidade_equivalente',
          max_pagamentos: '6',
          teto_percentual_mensalidade: '60',
          teto_valor: '',
          pagamentos_gerados: 0,
          valor_gerado: 0,
          observacao: '',
        },
      ],
    }));
  }

  function updateSalesResponsibility(index, patch) {
    setClientForm((prev) => ({
      ...prev,
      sales_responsibilities: prev.sales_responsibilities.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...patch } : item
      )),
    }));
  }

  function removeSalesResponsibility(index) {
    setClientForm((prev) => ({
      ...prev,
      sales_responsibilities: prev.sales_responsibilities.filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  async function saveSalesResponsibilities(clientId, responsibilities) {
    const normalized = normalizeSalesResponsibilities(responsibilities);
    const totalPercent = normalized.reduce((total, item) => total + item.percentual, 0);

    if (totalPercent > 100) {
      throw new Error(`Responsáveis pela venda não podem passar de 100%. Total atual: ${totalPercent}%.`);
    }
    const invalidPeriod = normalized.find((item) => item.fim_em && item.fim_em < item.inicio_em);
    if (invalidPeriod) {
      throw new Error('A data final da comissão não pode ser anterior à data inicial.');
    }
    const invalidLimit = normalized.find((item) => item.max_pagamentos < 1 || item.max_pagamentos > 120);
    if (invalidLimit) {
      throw new Error('O limite da comissão deve ficar entre 1 e 120 pagamentos.');
    }

    const existingIds = new Set(
      salesResponsibilities
        .filter((item) => item.cliente_mecsync_id === clientId)
        .map((item) => item.id),
    );
    const retainedIds = new Set(normalized.map((item) => item.id).filter(Boolean));
    const removedIds = [...existingIds].filter((id) => !retainedIds.has(id));

    if (removedIds.length > 0) {
      const { error: deactivateError } = await supabase
        .from('gdn_sales_responsibilities')
        .update({ ativo: false, deleted_at: new Date().toISOString() })
        .in('id', removedIds);
      if (deactivateError) throw deactivateError;
    }

    const existingItems = normalized.filter((item) => item.id);
    for (const item of existingItems) {
      const { id, ...payload } = item;
      const { error: updateError } = await supabase
        .from('gdn_sales_responsibilities')
        .update({ ...payload, ativo: true })
        .eq('id', id)
        .eq('cliente_mecsync_id', clientId);
      if (updateError) throw updateError;
    }

    const newItems = normalized.filter((item) => !item.id);
    if (newItems.length === 0) return;

    const { error: insertError } = await supabase
      .from('gdn_sales_responsibilities')
      .insert(newItems.map((item) => ({
        cliente_mecsync_id: clientId,
        participant_id: item.participant_id,
        percentual: item.percentual,
        valor_fixo: item.valor_fixo,
        inicio_em: item.inicio_em,
        fim_em: item.fim_em,
        base_calculo: item.base_calculo,
        max_pagamentos: item.max_pagamentos,
        teto_percentual_mensalidade: item.teto_percentual_mensalidade,
        teto_valor: item.teto_valor,
        observacao: item.observacao,
        ativo: true,
      })));

    if (insertError) throw insertError;
  }

  function updatePartnerDraft(applicationId, patch) {
    setPartnerDrafts((prev) => ({
      ...prev,
      [applicationId]: {
        ...(prev[applicationId] || {}),
        ...patch,
      },
    }));
  }

  async function updatePartnerStatus(application, status) {
    setSavingPartnerId(application.id);
    try {
      const { error } = await supabase
        .from('gdn_partner_applications')
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          review_notes: partnerDrafts[application.id]?.review_notes || application.review_notes || null,
        })
        .eq('id', application.id);

      if (error) throw error;
      toast.success(status === 'em_analise' ? 'Parceiro marcado para analise.' : 'Candidatura atualizada.');
      await load();
    } catch (error) {
      toast.error(error.message || 'Não foi possível atualizar o parceiro.');
    } finally {
      setSavingPartnerId(null);
    }
  }

  async function approvePartner(application) {
    const draft = partnerDrafts[application.id] || {};
    const defaultPercent = Number(String(draft.default_percentual || '').replace(',', '.'));
    const defaultPayments = Number(draft.default_max_pagamentos || 0);
    const referralCode = String(draft.referral_code || application.referral_code || createReferralCode(application.nome, application.email))
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);

    if (!Number.isFinite(defaultPercent) || defaultPercent <= 0 || defaultPercent > 100) {
      toast.error('Informe um percentual padrão entre 0,01% e 100%.');
      return;
    }
    if (!Number.isFinite(defaultPayments) || defaultPayments < 1 || defaultPayments > 120) {
      toast.error('Informe uma duração entre 1 e 120 pagamentos.');
      return;
    }
    if (!referralCode) {
      toast.error('Informe um código de indicação para o parceiro.');
      return;
    }

    setSavingPartnerId(application.id);
    try {
      let participantId = application.participant_id;
      if (!participantId) {
        const { data: participant, error: participantError } = await supabase
          .from('gdn_finance_participants')
          .insert({
            nome: application.nome,
            funcao: 'Parceiro comercial',
            percentual: 0,
            observacao: `Parceiro comercial aprovado. Email: ${application.email || '-'} | WhatsApp: ${application.telefone || '-'}`,
            prioridade_pagamento: 200,
            ativo: true,
          })
          .select('id')
          .single();

        if (participantError) throw participantError;
        participantId = participant.id;
      }

      const { error } = await supabase
        .from('gdn_partner_applications')
        .update({
          status: 'aprovado',
          participant_id: participantId,
          referral_code: referralCode,
          referral_active: true,
          default_percentual: Number(defaultPercent.toFixed(2)),
          default_max_pagamentos: Math.round(defaultPayments),
          reviewed_at: new Date().toISOString(),
          review_notes: draft.review_notes || application.review_notes || null,
          metadata: {
            ...(application.metadata || {}),
            default_percentual: Number(defaultPercent.toFixed(2)),
            default_max_pagamentos: Math.round(defaultPayments),
            referral_code: referralCode,
            approved_email: application.email,
          },
        })
        .eq('id', application.id);

      if (error) throw error;
      toast.success('Parceiro aprovado e criado no financeiro.');
      await load();
    } catch (error) {
      toast.error(error.message || 'Não foi possível aprovar o parceiro.');
    } finally {
      setSavingPartnerId(null);
    }
  }

  async function copyPartnerReferralLink(code) {
    const url = partnerReferralUrl(code);
    if (!url) {
      toast.error('Aprove o parceiro para gerar o link.');
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link de indicação copiado.');
    } catch {
      toast.error('Não foi possível copiar automaticamente.');
    }
  }

  function validateOfferForm() {
    const nome = offerForm.nome.trim();
    if (!nome) return 'Informe o nome da oferta.';
    if (!offerForm.plano_id) return 'Selecione o plano.';

    const valor = Number(String(offerForm.valor || '0').replace(',', '.'));
    if (!Number.isFinite(valor) || valor < 0) return 'Informe um valor válido.';

    if (offerForm.validade_inicio && offerForm.validade_fim && offerForm.validade_fim < offerForm.validade_inicio) {
      return 'A validade final não pode ser anterior à validade inicial.';
    }

    if (offerForm.limite_uso !== '' && Number(offerForm.limite_uso) < 1) {
      return 'O limite de usos deve ser de pelo menos 1.';
    }

    return null;
  }

  async function saveOffer() {
    if (savingOffer) return;

    const validationError = validateOfferForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const valor = Number(String(offerForm.valor || '0').replace(',', '.'));
    const trialDias = Math.max(Number(offerForm.trial_dias || 0), 0);
    const limiteUso = offerForm.limite_uso === '' ? null : Math.max(Number(offerForm.limite_uso), 1);

    setSavingOffer(true);

    try {
      const payload = {
        nome: offerForm.nome.trim(),
        plano_id: offerForm.plano_id,
        valor: Number(valor.toFixed(2)),
        moeda: String(offerForm.moeda || 'BRL').toUpperCase(),
        tipo_cobranca: offerForm.tipo_cobranca,
        trial_dias: trialDias,
        validade_inicio: offerForm.validade_inicio || null,
        validade_fim: offerForm.validade_fim || null,
        limite_uso: limiteUso,
        descricao: offerForm.descricao.trim() || null,
      };

      const selectClause = `
        *,
        planos_mecsync (
          id,
          codigo,
          nome,
          valor_mensal
        )
      `;

      if (isEditingOffer) {
        const { data, error } = await supabase
          .from('mecsync_sales_offers')
          .update(payload)
          .eq('id', offerForm.id)
          .select(selectClause)
          .single();

        if (error) throw error;

        setOffers((prev) => prev.map((item) => (item.id === data.id ? data : item)));
        toast.success('Oferta atualizada com sucesso.');
      } else {
        const token = crypto.randomUUID().replace(/-/g, '');

        const { data, error } = await supabase
          .from('mecsync_sales_offers')
          .insert({ ...payload, token, ativa: true })
          .select(selectClause)
          .single();

        if (error) throw error;

        setOffers((prev) => [data, ...prev]);
        toast.success('Oferta criada com sucesso.');
      }

      setOfferForm(emptyOfferForm);
    } catch (error) {
      console.error('Erro ao salvar oferta:', error);
      toast.error(error.message || 'Não foi possível salvar a oferta.');
    } finally {
      setSavingOffer(false);
    }
  }

  function startEditOffer(offer) {
    setOfferForm({
      id: offer.id,
      nome: offer.nome || '',
      plano_id: offer.plano_id || '',
      valor: offer.valor != null ? String(offer.valor) : '',
      moeda: offer.moeda || 'BRL',
      tipo_cobranca: offer.tipo_cobranca || 'monthly',
      trial_dias: offer.trial_dias ?? 30,
      validade_inicio: offer.validade_inicio ? String(offer.validade_inicio).split('T')[0] : '',
      validade_fim: offer.validade_fim ? String(offer.validade_fim).split('T')[0] : '',
      limite_uso: offer.limite_uso != null ? String(offer.limite_uso) : '',
      descricao: offer.descricao || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelEditOffer() {
    setOfferForm(emptyOfferForm);
  }

  async function removeOffer() {
    if (!deleteOfferTarget || deletingOffer) return;
    setDeletingOffer(true);
    try {
      const { error } = await supabase
        .from('mecsync_sales_offers')
        .delete()
        .eq('id', deleteOfferTarget.id);

      if (error) throw error;

      setOffers((prev) => prev.filter((item) => item.id !== deleteOfferTarget.id));
      if (offerForm.id === deleteOfferTarget.id) {
        setOfferForm(emptyOfferForm);
      }
      toast.success('Oferta excluída.');
      setDeleteOfferTarget(null);
    } catch (error) {
      console.error('Erro ao excluir oferta:', error);
      toast.error(error.message || 'Não foi possível excluir a oferta.');
    } finally {
      setDeletingOffer(false);
    }
  }

  function offerUrl(offer) {
    const baseUrl = process.env.REACT_APP_MECSYNC_PUBLIC_URL || 'https://mecsync.com.br';
    return `${baseUrl.replace(/\/$/, '')}/oferta/${offer.token}`;
  }

  async function copyOfferLink(offer) {
    const url = offerUrl(offer);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link da oferta copiado.');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  }

  async function toggleOffer(offer) {
    try {
      const { error } = await supabase
        .from('mecsync_sales_offers')
        .update({ ativa: !offer.ativa })
        .eq('id', offer.id);

      if (error) throw error;

      setOffers((prev) => prev.map((item) => (
        item.id === offer.id ? { ...item, ativa: !offer.ativa } : item
      )));

      toast.success(offer.ativa ? 'Oferta desativada.' : 'Oferta ativada.');
    } catch (error) {
      toast.error(error.message || 'Não foi possível atualizar a oferta.');
    }
  }

  const topOnline = useMemo(() => {
    const map = new Map();
    onlineBookings.forEach((item) => map.set(item.oficina_id, (map.get(item.oficina_id) || 0) + 1));
    return Array.from(map.entries())
      .map(([oficina_id, total]) => ({ oficina_id, total, client: rows.find((row) => row.oficina_id === oficina_id) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [onlineBookings, rows]);

  const planDistribution = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const planName = row.assinatura?.plano || 'Sem plano';
      const item = map.get(planName) || { plano: planName, clientes: 0, valor: 0 };
      item.clientes += 1;
      item.valor += Number(row.assinatura?.valor_mensal || 0);
      map.set(planName, item);
    });
    return Array.from(map.values()).sort((a, b) => b.valor - a.valor);
  }, [rows]);

  const dueSoonRows = useMemo(() => (
    rows
      .filter((row) => row.assinatura?.data_vencimento)
      .sort((a, b) => new Date(a.assinatura.data_vencimento) - new Date(b.assinatura.data_vencimento))
      .slice(0, 6)
  ), [rows]);

  const recentPayments = useMemo(() => payments.slice(0, 6), [payments]);

  function fillExistingOffice(oficinaId) {
    const office = offices.find((item) => item.id === oficinaId);
    const client = clients.find((item) => item.oficina_id === oficinaId);
    const assinatura = subscriptions.find((item) => item.cliente_mecsync_id === client?.id || item.oficina_id === oficinaId);
    const plan = plans.find((item) => item.id === assinatura?.plano_id || item.nome === assinatura?.plano || item.codigo === String(assinatura?.plano || '').toLowerCase());
    const adminUser = users.find((item) => item.oficina_id === oficinaId && item.perfil === 'admin') || users.find((item) => item.oficina_id === oficinaId);

    setClientForm((prev) => ({
      ...prev,
      oficina_id: oficinaId,
      nome_oficina: client?.nome_oficina || office?.nome || prev.nome_oficina,
      responsavel: client?.responsavel || adminUser?.nome || prev.responsavel,
      email: client?.email || adminUser?.email || office?.email || prev.email,
      telefone: client?.telefone || adminUser?.telefone || office?.telefone || prev.telefone,
      documento: client?.documento || prev.documento,
      pais_codigo: office?.pais_codigo || prev.pais_codigo,
      plano_id: plan?.id || prev.plano_id,
      perfil_conta: adminUser?.perfil === 'mecanico' ? 'mecanico' : 'admin',
      valor_mensal: assinatura?.valor_mensal ?? plan?.valor_mensal ?? prev.valor_mensal,
      moeda: assinatura?.moeda || prev.moeda,
      provedor_pagamento: assinatura?.provedor_pagamento || prev.provedor_pagamento,
      data_vencimento: assinatura?.data_vencimento || prev.data_vencimento,
      tipo_acesso: assinatura?.tipo_acesso || (assinatura?.status_pagamento === 'trial' ? 'trial' : 'paid'),
      trial_dias: 30,
      sales_responsibilities: client?.id ? salesResponsibilitiesForClient(client.id) : [],
    }));

    if (office || client) {
      toast.success('Dados da oficina carregados para atualização');
    }
  }

  async function removeMecSyncClient() {
    if (!deleteClient || deletingClient) return;
    setDeletingClient(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-client', {
        body: {
          client_id: deleteClient.id,
          type: 'mecsync_client',
          mode: 'deactivate',
        },
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Não foi possível desativar o cliente');
      }
      toast.success('Cliente desativado e acesso bloqueado.');
      setDeleteClient(null);
      load();
    } catch (error) {
      toast.error(error.message || 'Não foi possível desativar o cliente');
    } finally {
      setDeletingClient(false);
    }
  }

  async function removeUserPermanently() {
    if (!deleteUser) return;

    setDeletingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-user', {
        body: {
          email: deleteUser.email,
          profile_id: deleteUser.id,
          user_id: deleteUser.auth_user_id,
        },
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Não foi possível excluir o usuário.');
      }

      toast.success('Usuário excluído definitivamente.');
      setUsers((prev) => prev.filter((user) => user.id !== deleteUser.id && user.email !== deleteUser.email));
      setDeleteUser(null);
      load();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error(error.message || 'Não foi possível excluir o usuário.');
    } finally {
      setDeletingUser(false);
    }
  }

  async function sendPasswordReset() {
    if (!resetUser?.email) return;

    setResettingUser(true);
    setResetLink('');
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-user-password', {
        body: {
          email: resetUser.email,
          redirect_to: window.location.origin,
        },
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Nao foi possivel gerar o reset de senha.');
      }

      setResetLink(data?.action_link || '');
      toast.success('Link de reset gerado.');
      load();
    } catch (error) {
      console.error('Erro ao gerar reset de senha:', error);
      toast.error(error.message || 'Nao foi possivel gerar o reset de senha.');
    } finally {
      setResettingUser(false);
    }
  }

  async function blockSubscription() {
    if (!blockClient || !blockReason.trim()) {
      toast.error('Informe o motivo do bloqueio.');
      return;
    }

    setBlockingClient(true);
    try {
      const { error } = await supabase
        .from('assinaturas')
        .update({
          bloqueado_em: new Date().toISOString(),
          bloqueio_motivo: blockReason.trim(),
          status_pagamento: 'bloqueado',
        })
        .eq('oficina_id', blockClient.oficina_id);

      if (error) throw error;

      await logAdminAction('subscription_blocked', {
        type: 'subscription',
        id: blockClient.assinatura?.id,
        oficina_id: blockClient.oficina_id,
      }, {
        nome_oficina: blockClient.nome_oficina,
        motivo: blockReason.trim(),
      });

      toast.success('Assinatura bloqueada com sucesso.');
      setBlockClient(null);
      setBlockReason('');
      load();
    } catch (error) {
      console.error('Erro ao bloquear assinatura:', error);
      toast.error(error.message || 'Não foi possível bloquear a assinatura.');
    } finally {
      setBlockingClient(false);
    }
  }

  async function unblockSubscription() {
    if (!unblockClient) return;

    setUnblockingClient(true);
    try {
      const { error } = await supabase
        .from('assinaturas')
        .update({
          bloqueado_em: null,
          bloqueio_motivo: null,
          status_pagamento: 'ativo',
        })
        .eq('oficina_id', unblockClient.oficina_id);

      if (error) throw error;

      await logAdminAction('subscription_unblocked', {
        type: 'subscription',
        id: unblockClient.assinatura?.id,
        oficina_id: unblockClient.oficina_id,
      }, {
        nome_oficina: unblockClient.nome_oficina,
      });

      toast.success('Assinatura desbloqueada com sucesso.');
      setUnblockClient(null);
      load();
    } catch (error) {
      console.error('Erro ao desbloquear assinatura:', error);
      toast.error(error.message || 'Não foi possível desbloquear a assinatura.');
    } finally {
      setUnblockingClient(false);
    }
  }

  async function createOrUpdateAuthUser({ email, password, name, perfil }) {
    if (!password) return;
    if (password.length < 6) {
      throw new Error('Informe uma senha inicial com pelo menos 6 caracteres.');
    }

    const { data, error } = await supabase.functions.invoke('admin-create-user', {
      body: {
        email,
        password,
        name,
        perfil,
      },
    });

    if (error || data?.error) {
      throw new Error(data?.error || error?.message || 'Não foi possível criar o login do usuário.');
    }
  }

  async function saveClient() {
    setFormFeedback(null);

    if (!clientForm.nome_oficina || !clientForm.plano_id) {
      const message = 'Informe nome da oficina e plano.';
      setFormFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    if (salesResponsibilityTotal > 100) {
      const message = `Responsáveis pela venda não podem passar de 100%. Total atual: ${salesResponsibilityTotal}%.`;
      setFormFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    setSavingClient(true);
    try {
      const plan = selectedPlan;
      let oficinaId = clientForm.oficina_id.trim() || null;
      const officePayload = {
        nome: clientForm.nome_oficina.trim(),
        telefone: clientForm.telefone.trim() || null,
        email: clientForm.email.trim() || null,
        plano: plan?.codigo || plan?.nome?.toLowerCase() || 'starter',
        ...regionalPayload(clientForm.pais_codigo),
      };

      if (oficinaId) {
        const { error: officeUpdateError } = await supabase
          .from('oficinas')
          .update(officePayload)
          .eq('id', oficinaId);

        if (officeUpdateError) throw officeUpdateError;
      } else {
        const { data: office, error: officeError } = await supabase
          .from('oficinas')
          .insert({
            ...officePayload,
            slug: createSlug(clientForm.nome_oficina),
          })
          .select('id')
          .single();

        if (officeError) throw officeError;
        oficinaId = office.id;
      }

      const { data: client, error: clientError } = await supabase
        .from('mecsync_clientes')
        .upsert({
          oficina_id: oficinaId,
          nome_oficina: clientForm.nome_oficina.trim(),
          responsavel: clientForm.responsavel.trim() || null,
          email: clientForm.email.trim() || null,
          telefone: clientForm.telefone.trim() || null,
          documento: clientForm.documento.trim() || null,
          pais_codigo: clientForm.pais_codigo,
          status: 'ativo',
        }, { onConflict: 'oficina_id' })
        .select()
        .single();

      if (clientError) throw clientError;

      const isTrial = clientForm.tipo_acesso === 'trial';
      const trialStart = new Date();
      const trialEnd = new Date(trialStart.getTime() + Number(clientForm.trial_dias || 30) * 86400000);

      const { error: subscriptionError } = await supabase
        .from('assinaturas')
        .upsert({
          cliente_mecsync_id: client.id,
          oficina_id: oficinaId,
          plano_id: clientForm.plano_id,
          plano: plan?.nome || 'Starter',
          valor_mensal: Number(clientForm.valor_mensal || selectedPlanMonthlyValue || plan?.valor_mensal || 0),
          moeda: String(clientForm.moeda || 'BRL').toUpperCase(),
          provedor_pagamento: clientForm.provedor_pagamento || (String(clientForm.moeda || 'BRL').toUpperCase() === 'BRL' ? 'mercado_pago' : 'stripe'),
          data_vencimento: isTrial ? trialEnd.toISOString().split('T')[0] : (clientForm.data_vencimento || defaultDueDate()),
          status_pagamento: isTrial ? 'trial' : 'ativo',
          tipo_acesso: isTrial ? 'trial' : 'paid',
          trial_inicio: isTrial ? trialStart.toISOString().split('T')[0] : null,
          trial_fim: isTrial ? trialEnd.toISOString().split('T')[0] : null,
          bloqueado_em: null,
          bloqueio_motivo: null,
          mercado_pago_user_id: '3399433979',
          mercado_pago_application_id: '2918394379011719',
        }, { onConflict: 'oficina_id' });

      if (subscriptionError) throw subscriptionError;

      await saveSalesResponsibilities(client.id, clientForm.sales_responsibilities);

      const accountEmail = clientForm.email.trim().toLowerCase();
      if (accountEmail) {
        const existingUser = users.find((item) => item.email === accountEmail);
        if (!clientForm.senha && !existingUser) {
          throw new Error('Informe uma senha inicial para criar o login do admin.');
        }

        await createOrUpdateAuthUser({
          email: accountEmail,
          password: clientForm.senha,
          name: clientForm.responsavel.trim() || clientForm.nome_oficina.trim(),
          perfil: clientForm.perfil_conta,
        });

        const { error: userError } = await supabase
          .from('usuarios')
          .upsert({
            oficina_id: oficinaId,
            nome: clientForm.responsavel.trim() || clientForm.nome_oficina.trim(),
            email: accountEmail,
            telefone: clientForm.telefone.trim() || null,
            perfil: clientForm.perfil_conta,
            ativo: true,
          }, { onConflict: 'email' });

        if (userError) throw userError;
      }

      await logAdminAction('mecsync_client_saved', {
        type: 'mecsync_client',
        id: client.id,
        email: accountEmail || undefined,
        oficina_id: oficinaId,
      }, {
        nome_oficina: clientForm.nome_oficina.trim(),
        plano: plan?.nome || 'Starter',
        tipo_acesso: clientForm.tipo_acesso,
        valor_mensal: Number(clientForm.valor_mensal || plan?.valor_mensal || 0),
        moeda: String(clientForm.moeda || 'BRL').toUpperCase(),
      });

      const successMessage = isTrial
        ? 'Login de teste criado com validade definida.'
        : 'Cliente, assinatura e admin salvos com sucesso.';
      setFormFeedback({ type: 'success', message: successMessage });
      toast.success(successMessage);
      setClientForm(emptyClientForm);
      setEmployeeForm((prev) => ({ ...prev, oficina_id: oficinaId }));
      setActiveTab('clientes');
      load();
    } catch (error) {
      console.error('Erro ao salvar cliente MecSync:', error);
      const message = error.message ? `Não foi possível salvar: ${error.message}` : 'Não foi possível salvar o cliente.';
      setFormFeedback({ type: 'error', message });
      toast.error(message);
    } finally {
      setSavingClient(false);
    }
  }

  async function saveEmployee() {
    setEmployeeFeedback(null);

    const email = employeeForm.email.trim().toLowerCase();
    if (!employeeForm.oficina_id || !employeeForm.nome.trim() || !email) {
      const message = 'Escolha o cliente e informe nome e email do funcionário.';
      setEmployeeFeedback({ type: 'error', message });
      toast.error(message);
      return;
    }

    setSavingEmployee(true);
    try {
      const existingUser = users.find((item) => item.email === email);
      if (!employeeForm.senha && !existingUser) {
        throw new Error('Informe uma senha inicial para criar o login.');
      }

      await createOrUpdateAuthUser({
        email,
        password: employeeForm.senha,
        name: employeeForm.nome.trim(),
        perfil: employeeForm.perfil,
      });

      const { error } = await supabase
        .from('usuarios')
        .upsert({
          oficina_id: employeeForm.oficina_id,
          nome: employeeForm.nome.trim(),
          email,
          telefone: employeeForm.telefone.trim() || null,
          perfil: employeeForm.perfil,
          ativo: true,
        }, { onConflict: 'email' });

      if (error) throw error;

      const message = 'Funcionario salvo e vinculado ao cliente.';
      setEmployeeFeedback({ type: 'success', message });
      toast.success(message);
      setEmployeeForm((prev) => ({ ...emptyEmployeeForm, oficina_id: prev.oficina_id }));
      load();
    } catch (error) {
      console.error('Erro ao salvar funcionário:', error);
      const message = error.message ? `Não foi possível salvar: ${error.message}` : 'Não foi possível salvar o funcionário.';
      setEmployeeFeedback({ type: 'error', message });
      toast.error(message);
    } finally {
      setSavingEmployee(false);
    }
  }

  function feedbackBox(feedback) {
    if (!feedback) return null;
    return (
      <div className={`rounded-lg border px-3 py-2.5 text-sm ${feedback.type === 'success'
        ? 'border-success/30 bg-success/10 text-success'
        : 'border-danger/30 bg-danger/10 text-danger'
        }`}>
        {feedback.message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="card flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-sm font-bold text-accent ring-1 ring-accent/30">
            GDN
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight text-zinc-100">GDN Backoffice</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
              </span>
              ao vivo · {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.email && <span className="hidden max-w-[240px] truncate text-xs text-zinc-500 md:block">{user.email}</span>}
          <Button variant="ghost" onClick={handleLogout}>
            <LogOut size={15} />
            Sair
          </Button>
        </div>
      </header>

      <PageHeader
        title="Painel GDN"
        subtitle="Visão geral da operação, clientes, assinaturas e receita"
        actions={
          <Button onClick={load} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Atualizando...' : 'Atualizar'}
          </Button>
        }
      />

      <nav aria-label="Navegação do backoffice" className="card flex gap-1 overflow-x-auto p-1.5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              aria-current={active ? 'page' : undefined}
              onClick={() => setActiveTab(tab.id)}
              className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${active
                ? 'bg-accent/10 text-accent ring-1 ring-accent/30'
                : 'text-zinc-400 hover:bg-surface2 hover:text-zinc-100'}`}
            >
              <Icon size={15} className={active ? 'text-accent' : 'text-zinc-500'} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activeTab === 'dashboard' && (
        <div className="space-y-4">
          {loadWarnings.length > 0 && <Alert items={loadWarnings} />}

          {/* Linha 1 — receita e saúde da base */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Clientes ativos"
              value={loading ? '' : stats.active}
              icon={Building2}
              tone="accent"
              loading={loading}
              description={`${rows.length} cliente(s) na base`}
            />
            <MetricCard
              label="MRR previsto"
              value={loading ? '' : currency(stats.mrr)}
              icon={TrendingUp}
              tone="success"
              loading={loading}
              description="Soma das mensalidades ativas"
            />
            <MetricCard
              label="Recebido no mês"
              value={loading ? '' : currency(stats.paidThisMonth)}
              icon={Wallet}
              tone="info"
              loading={loading}
              description={`Recebido total: ${loading ? '—' : currency(stats.paid)}`}
            />
            <MetricCard
              label="Atrasados"
              value={loading ? '' : stats.late}
              icon={AlertTriangle}
              tone="danger"
              loading={loading}
              description={`${stats.pending} mensalidade(s) pendente(s)`}
            />
          </div>

          {/* Linha 2 — operação */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Assinaturas em dia" value={loading ? '' : stats.paidSubscriptions} icon={CheckCircle2} tone="info" loading={loading} />
            <MetricCard label="Funcionários" value={loading ? '' : stats.employeeCount} icon={Users} tone="default" loading={loading} />
            <MetricCard label="Parceiros pendentes" value={loading ? '' : stats.partnerQueue} icon={Handshake} tone="warning" loading={loading} description="Aguardando aprovação" />
            <MetricCard label="Ofertas ativas" value={loading ? '' : stats.activeOffers} icon={Tag} tone="accent" loading={loading} />
          </div>

          {/* Linha 3 — agenda online */}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Agenda online" value={loading ? '' : stats.onlineTotal} icon={CalendarClock} tone="default" loading={loading} description="Agendamentos recebidos" />
            <MetricCard label="Oficinas com agenda" value={loading ? '' : stats.onlineOffices} icon={Building2} tone="default" loading={loading} />
            <MetricCard label="Conversões em OS" value={loading ? '' : stats.converted} icon={CheckCircle2} tone="success" loading={loading} description="Agendamentos que viraram OS" />
            <MetricCard
              label="Taxa de conversão"
              value={loading ? '' : `${stats.onlineTotal ? Math.round((stats.converted / stats.onlineTotal) * 100) : 0}%`}
              icon={Activity}
              tone="accent"
              loading={loading}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-200">Faturamento por plano</h3>
                <span className="text-xs text-zinc-500">MRR ativo</span>
              </div>
              {planDistribution.length === 0 ? (
                <p className="text-sm text-zinc-500">Nenhum plano com cliente ativo.</p>
              ) : (
                <div className="space-y-3.5">
                  {(() => {
                    const maxValor = Math.max(...planDistribution.map((item) => Number(item.valor || 0)), 1);
                    return planDistribution.map((item) => (
                      <div key={item.plano}>
                        <div className="mb-1.5 flex items-baseline justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-sm font-medium text-zinc-100">
                              {isSynesPlan(item.plano) ? <PlanBrand plan={item.plano} compact /> : item.plano}
                            </span>
                            <span className="shrink-0 rounded-full bg-surface2 px-2 py-0.5 text-xs text-zinc-400">
                              {item.clientes} cliente(s)
                            </span>
                          </div>
                          <p className="shrink-0 text-sm font-semibold text-success">{currency(item.valor)}</p>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface2">
                          <div
                            className="h-full rounded-full bg-accent/70 transition-all duration-300"
                            style={{ width: `${Math.max((Number(item.valor || 0) / maxValor) * 100, 4)}%` }}
                          />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </Card>

            <Card>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-200">Próximos vencimentos</h3>
                <span className="text-xs text-zinc-500">{dueSoonRows.length} assinatura(s)</span>
              </div>
              {dueSoonRows.length === 0 ? (
                <p className="text-sm text-zinc-500">Nenhuma assinatura com vencimento.</p>
              ) : (
                <div className="divide-y divide-border">
                  {dueSoonRows.map((row) => {
                    const dueDate = row.assinatura?.data_vencimento
                      ? new Date(`${String(row.assinatura.data_vencimento).split('T')[0]}T00:00:00`)
                      : null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const dias = dueDate ? Math.round((dueDate.getTime() - today.getTime()) / 86400000) : null;
                    const vencido = dias !== null && dias < 0;
                    const proximo = dias !== null && !vencido && dias <= 3;
                    return (
                      <div key={row.id} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-zinc-100">{row.nome_oficina}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <Badge>{row.assinatura?.plano || 'Sem plano'}</Badge>
                            {vencido && <span className="text-xs font-medium text-danger">vencido há {Math.abs(dias)} dia(s)</span>}
                            {proximo && <span className="text-xs font-medium text-warning">{dias === 0 ? 'vence hoje' : `vence em ${dias} dia(s)`}</span>}
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className={`text-sm font-semibold ${vencido ? 'text-danger' : 'text-zinc-100'}`}>
                            {formatDate(row.assinatura?.data_vencimento)}
                          </p>
                          <p className="text-xs text-zinc-500">{billingCurrency(row.assinatura?.valor_mensal || 0, row.assinatura?.moeda)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <Card>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-200">Últimos pagamentos</h3>
                <span className="text-xs text-zinc-500">{recentPayments.length} registro(s)</span>
              </div>
              {recentPayments.length === 0 ? (
                <p className="py-4 text-sm text-zinc-500">Nenhum pagamento registrado.</p>
              ) : (
                <div className="divide-y divide-border">
                  {recentPayments.map((payment) => {
                    const client = rows.find((item) => item.oficina_id === payment.oficina_id || item.assinatura?.id === payment.assinatura_id);
                    const status = String(payment.status || '-');
                    const confirmed = status.toLowerCase().includes('approved') || status.toLowerCase().includes('paid') || status.toLowerCase().includes('succeeded');
                    return (
                      <div key={payment.id || payment.mercado_pago_payment_id} className="flex items-center justify-between gap-3 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-zinc-100">{client?.nome_oficina || payment.oficina_id || 'Cliente não localizado'}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium ${confirmed ? 'text-success' : 'text-zinc-500'}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${confirmed ? 'bg-success' : 'bg-zinc-600'}`} />
                              {status}
                            </span>
                            <span className="text-xs text-zinc-500">{formatDate(payment.criado_em || payment.created_at)}</span>
                          </div>
                        </div>
                        <p className="shrink-0 text-sm font-semibold text-zinc-100">
                          {billingCurrency(payment.valor || payment.transaction_amount || 0, payment.moeda)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-200">Oficinas mais ativas no agendamento online</h3>
                <span className="text-xs text-zinc-500">{topOnline.length} oficina(s)</span>
              </div>
              {topOnline.length === 0 ? (
                <p className="py-4 text-sm text-zinc-500">Nenhum agendamento online registrado ainda.</p>
              ) : (
                <div className="divide-y divide-border">
                  {topOnline.map((item, index) => (
                    <div key={item.oficina_id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface2 text-xs font-semibold text-zinc-400">
                          {index + 1}
                        </span>
                        <p className="truncate text-sm font-medium text-zinc-100">{item.client?.nome_oficina || item.oficina_id}</p>
                      </div>
                      <p className="shrink-0 text-sm text-zinc-400">{item.total} agendamento(s)</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'cadastro' && (
        <SectionCard
          icon={Building2}
          title="Cadastrar cliente, plano e admin"
          description="Crie a oficina, a assinatura e o acesso do administrador em um único passo."
        >
          <div className="grid gap-3 md:grid-cols-4">
            <Select
              value={clientForm.oficina_id}
              onChange={(event) => fillExistingOffice(event.target.value)}
              options={[{ value: '', label: 'Nova oficina ou buscar existente' }, ...officeOptions]}
            />
            <Input placeholder="Código interno da oficina (opcional)" value={clientForm.oficina_id} onChange={(event) => setClientForm((prev) => ({ ...prev, oficina_id: event.target.value }))} />
            <Input placeholder="Ex: Oficina Central" value={clientForm.nome_oficina} onChange={(event) => setClientForm((prev) => ({ ...prev, nome_oficina: event.target.value }))} />
            <Input placeholder="Nome do responsável/admin" value={clientForm.responsavel} onChange={(event) => setClientForm((prev) => ({ ...prev, responsavel: event.target.value }))} />
            <Input placeholder="(00) 00000-0000" mask="phone" value={clientForm.telefone} onChange={(event) => setClientForm((prev) => ({ ...prev, telefone: maskPhone(event.target.value) }))} />
            <Input type="email" placeholder="admin@oficina.com.br" value={clientForm.email} onChange={(event) => setClientForm((prev) => ({ ...prev, email: event.target.value }))} />
            <Input type="password" placeholder="Senha inicial do admin" value={clientForm.senha} autoComplete="new-password" onChange={(event) => setClientForm((prev) => ({ ...prev, senha: event.target.value }))} />
            <Input placeholder="CPF ou CNPJ" mask="cpfCnpj" value={clientForm.documento} onChange={(event) => setClientForm((prev) => ({ ...prev, documento: maskCpfCnpj(event.target.value) }))} />
            <Select
              value={clientForm.pais_codigo}
              onChange={(event) => {
                const regional = getCountryPreset(event.target.value);
                const nextValue = planMonthlyValue(selectedPlan, regional.currency);
                setClientForm((prev) => ({
                  ...prev,
                  pais_codigo: regional.countryCode,
                  moeda: regional.currency,
                  provedor_pagamento: regional.currency === 'BRL' ? 'mercado_pago' : 'stripe',
                  valor_mensal: nextValue || prev.valor_mensal,
                }));
              }}
              options={Object.keys(COUNTRY_PRESETS).map((code) => ({
                value: code,
                label: code === 'ES' ? 'España' : code === 'BR' ? 'Brasil' : `${code} - ${COUNTRY_PRESETS[code].currency}`,
              }))}
            />
            <Select
              value={clientForm.plano_id}
              onChange={(event) => {
                const plan = plans.find((item) => item.id === event.target.value);
                setClientForm((prev) => ({
                  ...prev,
                  plano_id: event.target.value,
                  valor_mensal: planMonthlyValue(plan, prev.moeda) || '',
                }));
              }}
              options={[{ value: '', label: 'Plano' }, ...plans.map((plan) => ({ value: plan.id, label: plan.nome }))]}
            />
            <Select
              value={clientForm.perfil_conta}
              onChange={(event) => setClientForm((prev) => ({ ...prev, perfil_conta: event.target.value }))}
              options={accountProfileOptions}
            />
            <Select
              value={clientForm.tipo_acesso}
              onChange={(event) => setClientForm((prev) => ({ ...prev, tipo_acesso: event.target.value }))}
              options={[
                { value: 'paid', label: 'Cliente pagante' },
                { value: 'trial', label: 'Teste grátis' },
              ]}
            />
            {clientForm.tipo_acesso === 'trial' && (
              <Input
                type="number"
                min="1"
                placeholder="Dias de teste"
                value={clientForm.trial_dias}
                onChange={(event) => setClientForm((prev) => ({ ...prev, trial_dias: event.target.value }))}
              />
            )}
            <Input placeholder="Ex: 299,90" mask="money" value={clientForm.valor_mensal} onChange={(event) => setClientForm((prev) => ({ ...prev, valor_mensal: normalizeMoney(event.target.value) }))} />
            <Input placeholder="Moeda (BRL, USD, EUR)" value={clientForm.moeda} onChange={(event) => setClientForm((prev) => {
              const moeda = event.target.value.toUpperCase().slice(0, 3);
              return { ...prev, moeda, provedor_pagamento: moeda === 'BRL' ? 'mercado_pago' : 'stripe' };
            })} />
            <Select
              value={clientForm.provedor_pagamento}
              onChange={(event) => setClientForm((prev) => ({ ...prev, provedor_pagamento: event.target.value }))}
              options={[
                { value: 'mercado_pago', label: 'Mercado Pago (BRL)' },
                { value: 'stripe', label: 'Stripe internacional' },
                { value: 'manual', label: 'Manual' },
              ]}
            />
            <Input type="date" value={clientForm.data_vencimento} onChange={(event) => setClientForm((prev) => ({ ...prev, data_vencimento: event.target.value }))} />
            <div className="md:col-span-4 rounded-lg border border-border bg-surface2 p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-white">Comissao dos responsaveis pela venda</h4>
                  <p className="text-sm text-zinc-400">Defina prazo, quantidade e teto. O historico ja gerado e preservado ao editar a regra.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge>{salesResponsibilityTotal}%</Badge>
                  <Button variant="ghost" onClick={addSalesResponsibility} type="button">
                    <UserPlus size={15} />
                    Adicionar
                  </Button>
                </div>
              </div>
              {clientForm.sales_responsibilities.length === 0 ? (
                <p className="text-sm text-zinc-500">Nenhum responsável vinculado. A mensalidade entra no financeiro sem participação de venda específica.</p>
              ) : (
                <div className="space-y-2">
                  {clientForm.sales_responsibilities.map((item, index) => (
                    <div key={`${item.id || 'new'}-${index}`} className="rounded-lg border border-border bg-surface p-3">
                      <div className="grid gap-2 md:grid-cols-4">
                        <Select
                          value={item.participant_id}
                          onChange={(event) => updateSalesResponsibility(index, { participant_id: event.target.value })}
                          options={[
                            { value: '', label: 'Responsavel' },
                            ...financeParticipants.map((participant) => ({
                              value: participant.id,
                              label: `${participant.nome}${participant.funcao ? ` - ${participant.funcao}` : ''}`,
                            })),
                          ]}
                        />
                        <Input type="number" min="0" max="100" placeholder="Comissao (%)" value={item.percentual} onChange={(event) => updateSalesResponsibility(index, { percentual: event.target.value })} />
                        <Input type="number" min="0" placeholder="Valor fixo adicional" value={item.valor_fixo} onChange={(event) => updateSalesResponsibility(index, { valor_fixo: event.target.value })} />
                        <Select
                          value={item.base_calculo}
                          onChange={(event) => updateSalesResponsibility(index, { base_calculo: event.target.value })}
                          options={[
                            { value: 'mensalidade_equivalente', label: 'Base: 1 mensalidade equivalente' },
                            { value: 'valor_recebido', label: 'Base: valor total recebido' },
                          ]}
                        />
                        <Input type="number" min="1" max="120" placeholder="Maximo de pagamentos" value={item.max_pagamentos} onChange={(event) => updateSalesResponsibility(index, { max_pagamentos: event.target.value })} />
                        <Input type="number" min="0.01" max="1000" placeholder="Teto (% de 1 mensalidade)" value={item.teto_percentual_mensalidade} onChange={(event) => updateSalesResponsibility(index, { teto_percentual_mensalidade: event.target.value })} />
                        <Input type="number" min="0" placeholder="Teto em valor (opcional)" value={item.teto_valor} onChange={(event) => updateSalesResponsibility(index, { teto_valor: event.target.value })} />
                        <Input placeholder="Observação" value={item.observacao} onChange={(event) => updateSalesResponsibility(index, { observacao: event.target.value })} />
                        <div>
                          <p className="mb-1 text-xs text-zinc-500">Inicio da regra</p>
                          <Input type="date" value={item.inicio_em} onChange={(event) => updateSalesResponsibility(index, { inicio_em: event.target.value })} />
                        </div>
                        <div>
                          <p className="mb-1 text-xs text-zinc-500">Fim da regra (opcional)</p>
                          <Input type="date" value={item.fim_em} onChange={(event) => updateSalesResponsibility(index, { fim_em: event.target.value })} />
                        </div>
                        <div className="flex items-center text-sm text-zinc-400">
                          Gerado: {item.pagamentos_gerados || 0}/{item.max_pagamentos || 0} pagamentos · {currency(item.valor_gerado || 0)}
                        </div>
                        <div className="flex justify-end">
                          <Button variant="ghost" type="button" className="text-red-300" onClick={() => removeSalesResponsibility(index)}>
                            <Trash2 size={15} />
                            Remover regra
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {salesResponsibilityTotal > 100 && (
                <p className="mt-2 text-sm text-red-300">Percentual acima de 100%. Ajuste antes de salvar.</p>
              )}
            </div>
            <Button className="justify-center md:col-span-2" onClick={saveClient} disabled={savingClient}>
              <Save size={15} />
              {savingClient ? 'Salvando...' : 'Salvar cliente e assinatura'}
            </Button>
            <div className="md:col-span-4">{feedbackBox(formFeedback)}</div>
          </div>
        </SectionCard>
      )}

      {activeTab === 'testes' && (
        <SectionCard
          icon={CalendarClock}
          title="Logins de teste grátis"
          description="Acompanhe trials, vencimentos e conversão para pagamento."
          actions={
            <Button onClick={() => {
              setClientForm({ ...emptyClientForm, tipo_acesso: 'trial', trial_dias: 30, data_vencimento: defaultDueDate() });
              setActiveTab('cadastro');
            }}>
              <UserPlus size={15} />
              Criar teste 30 dias
            </Button>
          }
        >
          <Table
            columns={['Oficina', 'Responsavel', 'Plano', 'Fim do teste', 'Status', 'Valor plano', 'Ações']}
            rows={rows.filter((row) => row.assinatura?.tipo_acesso === 'trial' || row.assinatura?.status_pagamento === 'trial')}
            loading={loading}
            emptyMessage="Nenhum teste grátis ativo ou recente"
            emptyIcon={CalendarClock}
            renderRow={(row) => {
              const trialEnd = row.assinatura?.trial_fim || row.assinatura?.data_vencimento;
              const expired = trialEnd ? new Date(`${String(trialEnd).split('T')[0]}T23:59:59`) < new Date() : false;
              return (
                <tr key={row.id} className="border-t border-border hover:bg-surface2">
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.nome_oficina}</div>
                    <div className="text-xs text-zinc-500">{row.email || row.telefone || row.oficina_id}</div>
                  </td>
                  <td className="px-3 py-2">{row.responsavel || '-'}</td>
                  <td className="px-3 py-2">
                    {isSynesPlan(row.assinatura?.plano) ? <PlanBrand plan={row.assinatura?.plano} compact /> : row.assinatura?.plano || '-'}
                  </td>
                  <td className="px-3 py-2">{formatDate(trialEnd)}</td>
                  <td className="px-3 py-2"><Badge>{expired ? 'expirado' : row.assinatura?.status_pagamento || 'trial'}</Badge></td>
                  <td className="px-3 py-2">{billingCurrency(row.assinatura?.valor_mensal || 0, row.assinatura?.moeda)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="ghost" onClick={() => {
                        fillExistingOffice(row.oficina_id);
                        setClientForm((prev) => ({ ...prev, tipo_acesso: 'paid', data_vencimento: defaultDueDate() }));
                        setActiveTab('cadastro');
                      }}>
                        Converter
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => {
                        fillExistingOffice(row.oficina_id);
                        setActiveTab('cadastro');
                      }}>
                        Editar
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            }}
          />
        </SectionCard>
      )}

      {activeTab === 'ofertas' && (
        <div className="space-y-4">
          <SectionCard
            icon={Tag}
            title={isEditingOffer ? 'Editar oferta comercial' : 'Criar oferta comercial'}
            description="Gere um link próprio para captar clientes com condições específicas."
            actions={isEditingOffer && (
              <Button variant="ghost" onClick={cancelEditOffer} type="button">
                <X size={15} />
                Cancelar edição
              </Button>
            )}
          >

            <div className="grid gap-3 md:grid-cols-3">
              <Input
                placeholder="Nome da oferta"
                value={offerForm.nome}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, nome: event.target.value }))}
              />

              <Select
                value={offerForm.plano_id}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, plano_id: event.target.value }))}
                options={[
                  { value: '', label: 'Selecione o plano' },
                  ...plans.map((plan) => ({ value: plan.id, label: plan.nome })),
                ]}
              />

              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Valor"
                value={offerForm.valor}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, valor: event.target.value }))}
              />

              <Select
                value={offerForm.moeda}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, moeda: event.target.value }))}
                options={[
                  { value: 'BRL', label: 'BRL — Real brasileiro' },
                  { value: 'USD', label: 'USD — Dólar' },
                  { value: 'EUR', label: 'EUR — Euro' },
                ]}
              />

              <Select
                value={offerForm.tipo_cobranca}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, tipo_cobranca: event.target.value }))}
                options={[
                  { value: 'monthly', label: 'Mensal' },
                  { value: 'lifetime', label: 'Vitalícia' },
                ]}
              />

              <Input
                type="number"
                min="0"
                placeholder="Dias grátis"
                value={offerForm.trial_dias}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, trial_dias: event.target.value }))}
              />

              <Input
                type="date"
                value={offerForm.validade_inicio}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, validade_inicio: event.target.value }))}
              />

              <Input
                type="date"
                value={offerForm.validade_fim}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, validade_fim: event.target.value }))}
              />

              <Input
                type="number"
                min="1"
                placeholder="Limite de usos (opcional)"
                value={offerForm.limite_uso}
                onChange={(event) => setOfferForm((prev) => ({ ...prev, limite_uso: event.target.value }))}
              />

              <div className="md:col-span-3">
                <Input
                  placeholder="Descrição da oferta"
                  value={offerForm.descricao}
                  onChange={(event) => setOfferForm((prev) => ({ ...prev, descricao: event.target.value }))}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              {isEditingOffer && (
                <Button variant="ghost" onClick={cancelEditOffer} type="button" disabled={savingOffer}>
                  Cancelar
                </Button>
              )}
              <Button onClick={saveOffer} disabled={savingOffer}>
                <Save size={15} />
                {savingOffer ? 'Salvando...' : isEditingOffer ? 'Salvar alterações' : 'Criar oferta'}
              </Button>
            </div>
          </SectionCard>

          <SectionCard icon={Tag} title="Ofertas existentes" description="Todas as campanhas de captação publicadas.">

            {loading ? (
              <p className="text-sm text-zinc-400">Carregando ofertas...</p>
            ) : offers.length === 0 ? (
              <p className="text-sm text-zinc-400">Nenhuma oferta cadastrada.</p>
            ) : (
              <div className="space-y-3">
                {offers.map((offer) => {
                  const url = offerUrl(offer);

                  return (
                    <div key={offer.id} className="rounded-lg border border-border bg-surface2 p-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold">{offer.nome}</h4>
                            <Badge>{offer.ativa ? 'Ativa' : 'Inativa'}</Badge>
                            <Badge>{offer.tipo_cobranca === 'lifetime' ? 'Vitalícia' : 'Mensal'}</Badge>
                          </div>

                          <p className="mt-1 text-sm text-zinc-400">
                            Plano: {offer.planos_mecsync?.nome || '-'}
                            {' · '}
                            {billingCurrency(offer.valor, offer.moeda)}
                            {' · '}
                            {offer.trial_dias || 0} dias grátis
                            {(offer.validade_inicio || offer.validade_fim) && (
                              <>
                                {' · '}
                                válida {offer.validade_inicio ? `de ${formatDate(offer.validade_inicio)}` : ''}
                                {offer.validade_fim ? ` até ${formatDate(offer.validade_fim)}` : ''}
                              </>
                            )}
                            {offer.limite_uso != null && (
                              <>{' · '}limite de {offer.limite_uso} uso(s)</>
                            )}
                          </p>

                          <p className="mt-2 break-all text-xs text-zinc-500">{url}</p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="ghost" onClick={() => copyOfferLink(offer)}>
                            <Copy size={14} />
                            Copiar link
                          </Button>

                          <Button size="sm" variant="ghost" onClick={() => startEditOffer(offer)}>
                            <Pencil size={14} />
                            Editar
                          </Button>

                          <Button size="sm" variant="ghost" onClick={() => toggleOffer(offer)}>
                            {offer.ativa ? (
                              <>
                                <Lock size={14} />
                                Desativar
                              </>
                            ) : (
                              <>
                                <Unlock size={14} />
                                Ativar
                              </>
                            )}
                          </Button>

                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => setDeleteOfferTarget(offer)}>
                            <Trash2 size={14} />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {activeTab === 'clientes' && (
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold">Clientes MecSync</h3>
              <p className="text-sm text-zinc-400">{filteredClientRows.length} de {rows.length} cliente(s) · clique na linha para ver detalhes</p>
            </div>
            <Button variant="ghost" onClick={exportClientsCsv} disabled={filteredClientRows.length === 0}>
              <Download size={15} />
              Exportar CSV
            </Button>
          </div>

          <div className="mb-4 grid gap-2 md:grid-cols-[1fr_180px_180px_180px]">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                className="h-10 w-full rounded-lg border border-border bg-surface2 pl-9 pr-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-orange-500/50 focus:outline-none"
                placeholder="Buscar por oficina, responsável, e-mail ou telefone..."
                value={clientSearch}
                onChange={(event) => setClientSearch(event.target.value)}
              />
            </div>
            <Select
              value={clientStatusFilter}
              onChange={(event) => setClientStatusFilter(event.target.value)}
              options={[
                { value: 'todos', label: 'Todos os status' },
                { value: 'trial', label: 'Teste grátis' },
                { value: 'ativo', label: 'Ativos/em dia' },
                { value: 'pendente', label: 'Pendentes' },
                { value: 'atrasado', label: 'Atrasados' },
                { value: 'bloqueado', label: 'Bloqueados' },
                { value: 'cancelado', label: 'Cancelados' },
              ]}
            />
            <Select
              value={clientPlanFilter}
              onChange={(event) => setClientPlanFilter(event.target.value)}
              options={[{ value: 'todos', label: 'Todos os planos' }, ...plans.map((plan) => ({ value: plan.nome, label: plan.nome }))]}
            />
            <Select
              value={clientSort}
              onChange={(event) => setClientSort(event.target.value)}
              options={[
                { value: 'nome', label: 'Ordenar: Oficina A-Z' },
                { value: 'vencimento', label: 'Ordenar: Vencimento' },
                { value: 'valor', label: 'Ordenar: Maior valor' },
              ]}
            />
          </div>

          <Table
            columns={['Oficina', 'Responsavel', 'Plano', 'Venda', 'Equipe', 'Vencimento', 'Status', 'Valor', 'Ações']}
            rows={filteredClientRows}
            loading={loading}
            emptyMessage={rows.length === 0 ? 'Nenhum cliente MecSync cadastrado' : 'Nenhum cliente encontrado com os filtros atuais'}
            emptyIcon={Building2}
            renderRow={(row) => (
              <tr
                key={row.id}
                className="cursor-pointer border-t border-border hover:bg-surface2"
                onClick={() => setDetailClient(row)}
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{row.nome_oficina}</div>
                  <div className="text-xs text-zinc-500">{row.email || row.telefone || row.oficina_id}</div>
                  {row.origem_venda === 'parceiro' && (
                    <div className="mt-1 text-xs text-orange-300">
                      Indicação: {row.referral_code || 'parceiro'}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">{row.responsavel || '-'}</td>
                <td className="px-3 py-2">
                  {isSynesPlan(row.assinatura?.plano) ? <PlanBrand plan={row.assinatura?.plano} compact /> : row.assinatura?.plano || '-'}
                </td>
                <td className="px-3 py-2">
                  {row.responsaveisVenda?.length ? (
                    <div className="space-y-1">
                      {row.responsaveisVenda.map((item) => (
                        <div key={item.id} className="text-xs text-zinc-300">
                          {item.participant_nome} {Number(item.percentual || 0) > 0 ? `(${item.percentual}%)` : ''}{Number(item.valor_fixo || 0) > 0 ? ` + ${currency(item.valor_fixo)}` : ''}
                          <span className="block text-zinc-500">
                            {item.pagamentos_gerados || 0}/{item.max_pagamentos || 0} pagamentos · teto {item.teto_percentual_mensalidade || 0}% de uma mensalidade
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : '-'}
                </td>
                <td className="px-3 py-2">{row.equipe?.length || 0}</td>
                <td className="px-3 py-2">{formatDate(row.assinatura?.data_vencimento)}</td>
                <td className="px-3 py-2">
                  <Badge className={row.assinatura?.status_pagamento === 'bloqueado' ? 'bg-red-500' : ''}>
                    {row.assinatura?.status_pagamento || row.status}
                  </Badge>
                  {row.assinatura?.bloqueado_em && (
                    <div className="mt-1 text-xs text-red-400">
                      Bloqueado em: {formatDate(row.assinatura.bloqueado_em)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">{billingCurrency(row.assinatura?.valor_mensal || 0, row.assinatura?.moeda)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2" onClick={(event) => event.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => setDetailClient(row)}>
                      <Eye size={14} /> Detalhes
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => {
                      fillExistingOffice(row.oficina_id);
                      setActiveTab('cadastro');
                    }}>
                      Editar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => {
                      setEmployeeForm((prev) => ({ ...prev, oficina_id: row.oficina_id }));
                      setActiveTab('equipe');
                    }}>
                      Equipe
                    </Button>
                    {row.assinatura?.status_pagamento === 'bloqueado' ? (
                      <Button size="sm" variant="ghost" className="text-green-400" onClick={() => setUnblockClient(row)}>
                        <Unlock size={14} /> Desbloquear
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-orange-400" onClick={() => setBlockClient(row)}>
                        <Lock size={14} /> Bloquear
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => setDeleteClient(row)}>
                      <Trash2 size={14} /> Desativar
                    </Button>
                  </div>
                </td>
              </tr>
            )}
          />
        </Card>
      )}

      {activeTab === 'equipe' && (
        <div className="grid gap-4 xl:grid-cols-[380px_1fr]">
          <SectionCard icon={UserPlus} title="Cadastrar funcionário" description="Crie o acesso e vincule o membro à oficina de um cliente.">
            <div className="space-y-3">
              <Select
                value={employeeForm.oficina_id}
                onChange={(event) => setEmployeeForm((prev) => ({ ...prev, oficina_id: event.target.value }))}
                options={[{ value: '', label: 'Cliente/oficina' }, ...officeOptions]}
              />
              <Input placeholder="Nome do funcionário" value={employeeForm.nome} onChange={(event) => setEmployeeForm((prev) => ({ ...prev, nome: event.target.value }))} />
              <Input type="email" placeholder="funcionário@oficina.com.br" value={employeeForm.email} onChange={(event) => setEmployeeForm((prev) => ({ ...prev, email: event.target.value }))} />
              <Input placeholder="(00) 00000-0000" mask="phone" value={employeeForm.telefone} onChange={(event) => setEmployeeForm((prev) => ({ ...prev, telefone: maskPhone(event.target.value) }))} />
              <Input type="password" placeholder="Senha inicial" value={employeeForm.senha} autoComplete="new-password" onChange={(event) => setEmployeeForm((prev) => ({ ...prev, senha: event.target.value }))} />
              <Select
                value={employeeForm.perfil}
                onChange={(event) => setEmployeeForm((prev) => ({ ...prev, perfil: event.target.value }))}
                options={employeeProfileOptions}
              />
              <Button className="w-full justify-center" onClick={saveEmployee} disabled={savingEmployee}>
                <UserPlus size={15} />
                {savingEmployee ? 'Salvando...' : 'Salvar funcionário'}
              </Button>
              {feedbackBox(employeeFeedback)}
              {selectedEmployeeOffice && (
                <p className="text-xs text-zinc-500">
                  Vinculando em: {selectedEmployeeOffice.nome_oficina}
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard icon={Users} title="Equipe vinculada aos clientes">
            <Table
              columns={['Nome', 'Email', 'Perfil', 'Cliente', 'Telefone', 'Ações']}
              rows={users}
              loading={loading}
              emptyMessage="Nenhum funcionário cadastrado"
              emptyIcon={Users}
              renderRow={(row) => {
                const client = rows.find((item) => item.oficina_id === row.oficina_id);
                return (
                  <tr key={row.id || row.email} className="border-t border-border hover:bg-surface2">
                    <td className="px-3 py-2">{row.nome || '-'}</td>
                    <td className="px-3 py-2">{row.email}</td>
                    <td className="px-3 py-2"><Badge>{row.perfil}</Badge></td>
                    <td className="px-3 py-2">{client?.nome_oficina || row.oficina_id || '-'}</td>
                    <td className="px-3 py-2">{row.telefone || '-'}</td>
                    <td className="px-3 py-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-blue-300"
                        onClick={() => {
                          setResetUser(row);
                          setResetLink('');
                        }}
                      >
                        <KeyRound size={14} /> Reset
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400"
                        onClick={() => setDeleteUser(row)}
                      >
                        <Trash2 size={14} /> Excluir definitivo
                      </Button>
                    </td>
                  </tr>
                );
              }}
            />
          </SectionCard>
        </div>
      )}

      {activeTab === 'parceiros' && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard label="Novos candidatos" value={loading ? '' : partnerApplications.filter((item) => item.status === 'novo').length} icon={UserPlus} tone="accent" loading={loading} />
            <MetricCard label="Em análise" value={loading ? '' : partnerApplications.filter((item) => item.status === 'em_analise').length} icon={AlertCircle} tone="info" loading={loading} />
            <MetricCard label="Aprovados" value={loading ? '' : partnerApplications.filter((item) => item.status === 'aprovado').length} icon={CheckCircle2} tone="success" loading={loading} />
          </div>

          <SectionCard
            icon={Handshake}
            title="Parceiros comerciais MecSync"
            description="Aprove candidatos, defina percentual e duração padrão, e transforme o parceiro em participante financeiro da GDN."
            actions={
              <Button variant="ghost" onClick={() => window.open('/parceiros', '_blank', 'noopener,noreferrer')}>
                <Handshake size={15} />
                Abrir cadastro publico
              </Button>
            }
          >

            <Table
              columns={['Parceiro', 'Identidade', 'Contato', 'Perfil comercial', 'Regra padrão', 'Status', 'Ações']}
              rows={partnerApplications}
              loading={loading}
              emptyMessage="Nenhuma candidatura de parceiro recebida"
              emptyIcon={Handshake}
              renderRow={(application) => {
                const draft = partnerDrafts[application.id] || {};
                const isSaving = savingPartnerId === application.id;
                const participant = application.gdn_finance_participants;
                const referralCode = draft.referral_code || application.referral_code || '';
                const referralUrl = partnerReferralUrl(referralCode);
                return (
                  <tr key={application.id} className="border-t border-border align-top hover:bg-surface2">
                    <td className="px-3 py-3">
                      <div className="font-medium">{application.nome}</div>
                      <div className="text-xs text-zinc-500">
                        {application.cidade || '-'}{application.estado ? `/${application.estado}` : ''} - {formatDate(application.created_at)}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm text-zinc-300">
                        {(application.documento_tipo || 'doc').toUpperCase()}: {application.documento_numero || '-'}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500">
                        Nasc.: {formatDate(application.data_nascimento)}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500">
                        {application.endereco || '-'}{application.bairro ? `, ${application.bairro}` : ''}{application.cep ? ` - CEP ${application.cep}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm">{application.email}</div>
                      <div className="text-xs text-zinc-500">{application.telefone || '-'}</div>
                      {application.redes_sociais && (
                        <div className="mt-1 text-xs text-zinc-500">{application.redes_sociais}</div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="text-sm text-zinc-300">{application.experiencia || '-'}</div>
                      <div className="mt-1 text-xs text-zinc-500">
                        Vende software/marketing: {application.vende_software === true ? 'sim' : application.vende_software === false ? 'nao' : '-'}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500">
                        Canais: {(application.canais_venda || []).join(', ') || '-'}
                      </div>
                      {application.carteira_oficinas !== null && application.carteira_oficinas !== undefined && (
                        <div className="mt-1 text-xs text-zinc-500">{application.carteira_oficinas} oficinas na carteira</div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="grid min-w-[220px] gap-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            min="0.01"
                            max="100"
                            placeholder="%"
                            value={draft.default_percentual ?? ''}
                            onChange={(event) => updatePartnerDraft(application.id, { default_percentual: event.target.value })}
                          />
                          <Input
                            type="number"
                            min="1"
                            max="120"
                            placeholder="Pagamentos"
                            value={draft.default_max_pagamentos ?? ''}
                            onChange={(event) => updatePartnerDraft(application.id, { default_max_pagamentos: event.target.value })}
                          />
                        </div>
                        <Input
                          placeholder="Observação interna"
                          value={draft.review_notes ?? ''}
                          onChange={(event) => updatePartnerDraft(application.id, { review_notes: event.target.value })}
                        />
                        <Input
                          placeholder="Código de indicação"
                          value={referralCode}
                          onChange={(event) => updatePartnerDraft(application.id, { referral_code: event.target.value })}
                        />
                        {participant && (
                          <p className="text-xs text-green-400">
                            Financeiro: {participant.nome}{participant.funcao ? ` - ${participant.funcao}` : ''}
                          </p>
                        )}
                        {application.status === 'aprovado' && referralUrl && (
                          <div className="rounded-md border border-green-500/20 bg-green-500/10 p-2">
                            <p className="break-all text-xs text-green-200">{referralUrl}</p>
                            <Button size="sm" variant="ghost" className="mt-2" onClick={() => copyPartnerReferralLink(referralCode)}>
                              <Copy size={14} />
                              Copiar link
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3"><Badge>{application.status}</Badge></td>
                    <td className="px-3 py-3">
                      <div className="flex min-w-[210px] flex-wrap gap-2">
                        {application.status === 'novo' && (
                          <Button size="sm" variant="ghost" onClick={() => updatePartnerStatus(application, 'em_analise')} disabled={isSaving}>
                            <AlertCircle size={14} />
                            Analisar
                          </Button>
                        )}
                        {application.status !== 'aprovado' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approvePartner(application)} disabled={isSaving}>
                            <CheckCircle2 size={14} />
                            Aprovar
                          </Button>
                        )}
                        {application.status === 'aprovado' && (
                          <Button size="sm" variant="ghost" onClick={() => approvePartner(application)} disabled={isSaving}>
                            <Save size={14} />
                            Salvar regra
                          </Button>
                        )}
                        {application.status !== 'aprovado' && application.status !== 'reprovado' && (
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => updatePartnerStatus(application, 'reprovado')} disabled={isSaving}>
                            <XCircle size={14} />
                            Reprovar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }}
            />
          </SectionCard>
        </div>
      )}

      {activeTab === 'financeiro' && (
        <GDNFinancePanel plans={plans} />
      )}

      {activeTab === 'auditoria' && (
        <SectionCard
          icon={Shield}
          title="Auditoria administrativa"
          description="Ações sensíveis feitas por usuários GDN e administradores."
          actions={
            <Button variant="ghost" onClick={load} disabled={loading}>
              <RefreshCw size={15} />
              Atualizar
            </Button>
          }
        >
          <Table
            columns={['Data', 'Ator', 'Ação', 'Alvo', 'Detalhes']}
            rows={auditLogs}
            loading={loading}
            emptyMessage="Nenhum evento de auditoria registrado"
            emptyIcon={Shield}
            renderRow={(row) => (
              <tr key={row.id} className="border-t border-border align-top hover:bg-surface2">
                <td className="px-3 py-2">{formatDate(row.created_at)}</td>
                <td className="px-3 py-2">
                  <div>{row.actor_email || '-'}</div>
                  <div className="text-xs text-zinc-500">{row.actor_perfil || '-'}</div>
                </td>
                <td className="px-3 py-2"><Badge>{row.action}</Badge></td>
                <td className="px-3 py-2">
                  <div>{row.target_email || row.target_id || '-'}</div>
                  <div className="text-xs text-zinc-500">{row.target_type || '-'}</div>
                </td>
                <td className="max-w-md px-3 py-2 text-xs text-zinc-400">
                  {JSON.stringify(row.details || {})}
                </td>
              </tr>
            )}
          />
        </SectionCard>
      )}

      <GDNClientDetailModal
        client={detailClient}
        payments={payments}
        onClose={() => setDetailClient(null)}
        onEdit={(row) => {
          setDetailClient(null);
          fillExistingOffice(row.oficina_id);
          setActiveTab('cadastro');
        }}
      />

      <Modal open={Boolean(blockClient)} onClose={() => setBlockClient(null)} title="Bloquear acesso do cliente">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Deseja bloquear a assinatura de <strong>{blockClient?.nome_oficina}</strong>?
          </p>
          <Input
            placeholder="Motivo do bloqueio"
            value={blockReason}
            onChange={(event) => setBlockReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBlockClient(null)} disabled={blockingClient}>Cancelar</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={blockSubscription} disabled={blockingClient}>
              {blockingClient ? 'Bloqueando...' : 'Bloquear assinatura'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(unblockClient)} onClose={() => setUnblockClient(null)} title="Desbloquear acesso">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Deseja desbloquear a assinatura de <strong>{unblockClient?.nome_oficina}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setUnblockClient(null)} disabled={unblockingClient}>Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={unblockSubscription} disabled={unblockingClient}>
              {unblockingClient ? 'Desbloqueando...' : 'Desbloquear assinatura'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(deleteClient)} onClose={() => setDeleteClient(null)} title="Desativar cliente">
        <div className="space-y-4">
          <p className="text-zinc-300">Deseja desativar <strong>{deleteClient?.nome_oficina}</strong>?</p>
          <p className="text-sm text-zinc-500">Esta ação cancela a assinatura e desativa os usuários, mantendo histórico e dados para auditoria.</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteClient(null)} disabled={deletingClient}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={removeMecSyncClient} disabled={deletingClient}>{deletingClient ? 'Desativando...' : 'Desativar cliente'}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(deleteUser)} onClose={() => setDeleteUser(null)} title="Excluir usuário definitivamente">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Deseja apagar <strong>{deleteUser?.nome || deleteUser?.email}</strong> definitivamente?
          </p>
          <p className="text-sm text-zinc-500">
            Esta ação remove o acesso e o cadastro do usuário. Depois disso, a pessoa não consegue mais entrar no sistema.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteUser(null)} disabled={deletingUser}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={removeUserPermanently} disabled={deletingUser}>
              {deletingUser ? 'Excluindo...' : 'Excluir definitivamente'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(resetUser)} onClose={() => { setResetUser(null); setResetLink(''); }} title="Resetar senha do usuário">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Gerar link de redefinição de senha para <strong>{resetUser?.nome || resetUser?.email}</strong>?
          </p>
          <p className="text-sm text-zinc-500">
            Use este fluxo em vez de definir senha manualmente. Envie o link gerado por um canal seguro.
          </p>
          {resetLink && (
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
              <p className="text-sm font-semibold text-blue-200">Link gerado</p>
              <p className="mt-2 break-all text-xs text-blue-100">{resetLink}</p>
              <Button
                size="sm"
                variant="ghost"
                className="mt-3"
                onClick={() => navigator.clipboard?.writeText(resetLink)}
              >
                <Copy size={14} />
                Copiar link
              </Button>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setResetUser(null); setResetLink(''); }} disabled={resettingUser}>Fechar</Button>
            <Button onClick={sendPasswordReset} disabled={resettingUser}>
              <KeyRound size={15} />
              {resettingUser ? 'Gerando...' : 'Gerar link'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={Boolean(deleteOfferTarget)} onClose={() => setDeleteOfferTarget(null)} title="Excluir oferta">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Deseja excluir a oferta <strong>{deleteOfferTarget?.nome}</strong>?
          </p>
          <p className="text-sm text-zinc-500">
            O link atual da oferta deixará de funcionar. Esta ação não pode ser desfeita.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteOfferTarget(null)} disabled={deletingOffer}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={removeOffer} disabled={deletingOffer}>
              {deletingOffer ? 'Excluindo...' : 'Excluir oferta'}
            </Button>
          </div>
        </div>
      </Modal>

      <footer className="mt-2 flex flex-col items-center justify-between gap-3 border-t border-border px-1 py-6 text-xs text-zinc-500 sm:flex-row sm:px-2">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-400">GDN Corp © {new Date().getFullYear()}</span>
          <span className="h-3 w-px bg-border" />
          <span>MecSync v0.1.0</span>
        </div>
        <div className="flex items-center gap-4">
          <a
            href="https://mecsync.com.br"
            target="_blank"
            rel="noreferrer"
            className="transition-colors duration-150 hover:text-zinc-300"
          >
            Premium IA
          </a>
          <a
            href="https://wa.me/5551999999999"
            target="_blank"
            rel="noreferrer"
            className="transition-colors duration-150 hover:text-zinc-300"
          >
            Suporte
          </a>
          <a
            href="https://mecsync.com.br/sobre"
            target="_blank"
            rel="noreferrer"
            className="transition-colors duration-150 hover:text-zinc-300"
          >
            Sobre
          </a>
        </div>
      </footer>
    </div>
  );
}